// Centralizes how the shell classifies backend denial responses into typed errors, so every
// scoped fetch path (useScopeData, useScopedFetch/getJson, app-shell/api/* wrappers) reacts to
// 401/403/404 identically instead of each throwing an opaque Error(`HTTP ${status}`) that a
// renderer can't route anywhere. Row 3.11 / S6 denial-surface consolidation.
import type { ForbiddenReason } from "../components/ShellForbidden";

// A real, disclosed-resource deny (existence already known to the caller). Backend 403 bodies on
// walled endpoints carry `{code, message}` — the ONE deny shape the wall ever returns as a real
// 403; maps to ShellForbidden's role/zone/overlay reasons, never "sealed".
export class ForbiddenError extends Error {
  code?: string;
  constructor(code?: string, message?: string) {
    super(message ?? "Forbidden");
    this.name = "ForbiddenError";
    this.code = code;
  }
}

// A walled endpoint's 404: the resource is missing OR the reader is sealed off from it, and the
// backend deliberately makes these indistinguishable (wall.py's own invariant: "a 403 would
// confirm existence"). Maps to ShellForbidden's "sealed" reason — never disclose which case it was.
export class ScopeSealedOrMissingError extends Error {
  constructor() {
    super("resource not found or not visible at this scope");
    this.name = "ScopeSealedOrMissingError";
  }
}

// A 401 with no engagement-specific code: the session cookie is absent/invalid (S1 surface). The
// caller isn't unauthorized, they're unauthenticated — the shell re-establishes a session, it
// doesn't show a permission-denied panel (that would be the wrong signal for the wrong cause).
export class SessionExpiredError extends Error {
  constructor() {
    super("session expired or not present");
    this.name = "SessionExpiredError";
  }
}

// A 422 typed-validation failure. Carries the backend's stable `code` (e.g. INVALID_SORT_FIELD) and any
// per-field errors, so a renderer routes it as "fix your input" — NEVER string-sniffed from the message.
// Terminal (not retryable): re-issuing the same request repeats the failure.
export class ValidationError extends Error {
  code?: string;
  fields?: Record<string, string>;
  constructor(code?: string, message?: string, fields?: Record<string, string>) {
    super(message ?? "Validation failed");
    this.name = "ValidationError";
    this.code = code;
    this.fields = fields;
  }
}

// A 5xx server failure. Retryable BY DEFAULT — the same request may succeed later (transient), so a
// renderer offers "retry" rather than "fix your input". Carries the HTTP status for observability,
// not for classification.
//
// `retryable: false` is the documented exception, and it is the server's call, never the renderer's:
// some 5xx failures are an upstream refusing us for a configuration reason and will fail identically
// forever. On 2026-08-30 a 465 MB recording failed every part on an Azure `AuthorizationFailure`
// because the storage account's network ACL did not include the caller's address. The upload surface
// showed "retry the same file" with a Retry button — advice that could not succeed, in front of a
// client. The backend now answers that case 502 STORAGE_NOT_AUTHORIZED with `retryable: false`.
//
// `code` is carried for the same reason ConflictError carries it: so a renderer routes the SPECIFIC
// failure without string-sniffing the message.
export class ServerError extends Error {
  status: number;
  code?: string;
  retryable: boolean;
  constructor(status: number, message?: string, code?: string, retryable = true) {
    super(message ?? `Server error (HTTP ${status})`);
    this.name = "ServerError";
    this.status = status;
    this.code = code;
    this.retryable = retryable;
  }
}

// A 409 conflict — the request collided with server state. Carries the backend's stable `code` so a
// renderer routes the SPECIFIC conflict (DUPLICATE_SOURCE / SOURCE_HAS_DERIVED_MEANING /
// SOURCE_UNDER_LEGAL_HOLD / NOT_QUARANTINED / RETRY_NOT_ALLOWED / …) WITHOUT sniffing the message.
// Terminal by default (retrying the same request repeats the conflict).
export class ConflictError extends Error {
  code?: string;
  constructor(code?: string, message?: string) {
    super(message ?? "Conflict");
    this.name = "ConflictError";
    this.code = code;
  }
}

/** A disclosed capability is intentionally unavailable, distinct from a sealed scope. */
export class CapabilityUnavailableError extends Error {
  code: string;

  constructor(code: string, message?: string) {
    super(message ?? "capability unavailable");
    this.name = "CapabilityUnavailableError";
    this.code = code;
  }
}

/** A request body exceeded the authenticated endpoint's configured upload limit. */
export class PayloadTooLargeError extends Error {
  constructor() {
    super("request payload is too large");
    this.name = "PayloadTooLargeError";
  }
}

/** The endpoint rejected the attachment's declared or detected media type. */
export class UnsupportedMediaTypeError extends Error {
  constructor() {
    super("request media type is unsupported");
    this.name = "UnsupportedMediaTypeError";
  }
}

/** A rate limit was reached. retryAfterSeconds comes only from the standard response header. */
export class RateLimitedError extends Error {
  retryAfterSeconds?: number;

  constructor(retryAfterSeconds?: number) {
    super("request rate limit reached");
    this.name = "RateLimitedError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

async function parseCodedBody(
  res: Response,
): Promise<{ code?: string; message?: string; retryable?: boolean }> {
  try {
    const data = (await res.json()) as {
      detail?: { code?: string; message?: string; retryable?: boolean } | string;
    };
    if (typeof data.detail === "string") return { message: data.detail };
    return {
      code: data.detail?.code,
      message: data.detail?.message,
      // Read only when the server states it. An absent flag must stay undefined rather than
      // defaulting to false here — every existing endpoint omits it, and treating silence as
      // "not retryable" would strip the retry from genuinely transient 5xx failures.
      retryable: data.detail?.retryable,
    };
  } catch {
    return {};
  }
}

async function readErrorDetail(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { detail?: unknown };
    return typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail ?? "");
  } catch {
    return "";
  }
}

/**
 * Classify a non-ok fetch Response into the shell's typed denial vocabulary. Callers await this
 * INSTEAD OF throwing a generic Error: 401 -> SessionExpiredError, 403 -> ForbiddenError (with the
 * backend's code/message), 404 -> ScopeSealedOrMissingError, anything else -> a plain Error carrying
 * the raw status (unclassified failures
 * stay unclassified - a 422/500 is not a denial signal and must not be dressed up as one).
 */
export async function classifyFetchError(res: Response, context: string): Promise<Error> {
  if (res.status === 401) return new SessionExpiredError();
  if (res.status === 403) {
    const { code, message } = await parseCodedBody(res);
    return new ForbiddenError(code, message);
  }
  if (res.status === 404) {
    return new ScopeSealedOrMissingError();
  }
  if (res.status === 409) {
    const { code, message } = await parseCodedBody(res);
    return new ConflictError(code, message);
  }
  if (res.status === 422) {
    const { code, message } = await parseCodedBody(res);
    return new ValidationError(code, message);
  }
  if (res.status === 413) return new PayloadTooLargeError();
  if (res.status === 415) return new UnsupportedMediaTypeError();
  if (res.status === 429) {
    const retryAfter = Number.parseInt(res.headers.get("Retry-After") ?? "", 10);
    return new RateLimitedError(Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined);
  }
  if (res.status >= 500) {
    // Carry the server's own code and retryable verdict through. `retryable ?? true` keeps the
    // documented default — a 5xx is retryable unless the server explicitly says it is not — so
    // every endpoint that omits the field behaves exactly as before.
    const { code, message, retryable } = await parseCodedBody(res);
    return new ServerError(res.status, message, code, retryable ?? true);
  }
  return new Error(`${context} failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
}

// Known backend 403 codes -> the D-UAM-04 reason they represent (doc 15 §8). Every code below is a
// disclosed-resource deny (the caller already knows the resource exists) — "zone" is reserved for
// a workspace/barrier-boundary-shaped denial; everything else defaults to "role" until a backend
// code needing "overlay"/"flag" semantics exists (there is none today — verified by a repo-wide
// grep of every `status_code=403` site, 2026-07-08). Replaces the previous stub that ignored its
// `code` argument entirely and always returned "role".
const FORBIDDEN_CODE_REASON: Record<string, ForbiddenReason> = {
  SOURCE_QUARANTINED: "role",
  ACTIVITY_WRITE_FORBIDDEN: "role",
  FORBIDDEN: "role",
};

export function forbiddenReasonForCode(code?: string): ForbiddenReason {
  if (!code) return "role";
  return FORBIDDEN_CODE_REASON[code] ?? "role";
}
